import React from "react";
import { Lock } from "lucide-react";
import { useFinancePin } from "@/context/FinancePinContext";

/**
 * FinancialValue — masks a monetary string when the finance PIN gate is
 * enabled and not unlocked. Stays a thin wrapper so it composes with the
 * existing `fmtEur(...)` helpers each page already uses:
 *
 *     <FinancialValue value={fmtEur(stats.revenue_potential)} />
 *
 * Renders the formatted value verbatim when:
 *   • the user has no PIN configured, OR
 *   • the PIN is currently unlocked.
 *
 * Otherwise renders a fixed-width bullet pattern (`•••••••`) that mimics
 * the typographic weight of a 4–6 digit euro figure so the layout doesn't
 * jump on lock/unlock transitions. The mask carries a `data-locked` flag
 * for tests and a `title` so the operator gets a hover hint on desktop.
 */
export default function FinancialValue({
  value,
  className = "",
  testid,
}) {
  const { enabled, unlocked } = useFinancePin();
  const masked = enabled && !unlocked;
  if (!masked) {
    return (
      <span className={className} data-testid={testid}>
        {value}
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-2 ${className}`}
      data-testid={testid}
      data-locked="true"
      title="Dato protetto da PIN economico"
    >
      <Lock className="w-3.5 h-3.5 text-neutral-400" strokeWidth={1.5} aria-hidden="true" />
      <span aria-label="Valore protetto" className="tracking-widest text-neutral-400 select-none">
        ••••••
      </span>
    </span>
  );
}
